/**
 * GuidelineListPanel — left rail listing the available guidelines, searchable
 * and grouped by program (RTL / GUC / …). Selecting one navigates the reader.
 */
import { useMemo, useState } from "react";

import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import LockOutlined from "@mui/icons-material/LockOutlined";
import MenuBookOutlined from "@mui/icons-material/MenuBookOutlined";
import UploadFileOutlined from "@mui/icons-material/UploadFileOutlined";

import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import SearchInput from "../../../components/inputs/SearchInput";

function GuidelineRow({ doc, selected, onSelect }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={() => onSelect(doc.slug)}
      sx={{
        appearance: "none",
        textAlign: "left",
        cursor: "pointer",
        width: "100%",
        border: "1px solid",
        borderColor: selected ? "#1F5AF2" : "transparent",
        bgcolor: selected ? "#f1f6f9" : "transparent",
        borderRadius: 1.5,
        px: 1.25,
        py: 0.65,
        mb: 0.25,
        transition: "background 0.12s, border-color 0.12s",
        "&:hover": { bgcolor: selected ? "#e4eef3" : "#e8eef2" },
      }}
    >
      <Flx ac g={0.6} wrap sx={{ minWidth: 0 }}>
        <Txt
          sx={{
            fontSize: 12,
            fontWeight: selected ? 700 : 600,
            color: "#0f172a",
            lineHeight: 1.25,
          }}
        >
          {doc.title}
        </Txt>
        {doc.locked && (
          <LockOutlined
            sx={{ fontSize: 12, color: "#94a3b8", flexShrink: 0 }}
          />
        )}
      </Flx>
      <Flx ac g={0.75} sx={{ mt: 0.25 }}>
        <Chip
          label={doc.program || "RTL"}
          size="small"
          sx={{
            height: 17,
            fontSize: 10,
            fontWeight: 600,
            bgcolor: "#e8eef2",
            color: "#1F5AF2",
            "& .MuiChip-label": { px: 0.75 },
          }}
        />
        <Txt sx={{ fontSize: 11, color: "#94a3b8" }}>v{doc.version}</Txt>
      </Flx>
    </Box>
  );
}

export default function GuidelineListPanel({
  list,
  loading,
  selectedSlug,
  onSelect,
  onImport,
}) {
  const [search, setSearch] = useState("");

  const groups = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? list.filter(
          (g) =>
            g.title?.toLowerCase().includes(q) ||
            g.program?.toLowerCase().includes(q),
        )
      : list;
    const byProgram = new Map();
    for (const g of filtered) {
      const key = g.program || "Other";
      if (!byProgram.has(key)) byProgram.set(key, []);
      byProgram.get(key).push(g);
    }
    return [...byProgram.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [list, search]);

  return (
    <Flx
      column
      sx={{
        height: "100%",
        minHeight: 0,
        borderRight: "1px solid #e5e7eb",
        bgcolor: "#fcfcfd",
      }}
    >
      <Box sx={{ px: 1.75, pt: 2, pb: 1.5, borderBottom: "1px solid #d8dee4" }}>
        <Flx ac jb g={1} sx={{ mb: 1.25 }}>
          <Txt
            sx={{
              fontSize: 14,
              fontWeight: 700,
              letterSpacing: "0.07em",
              textTransform: "uppercase",
              color: "#64748b",
            }}
          >
            Guidelines
          </Txt>
          {onImport && (
            <Tooltip title="Import PDF as new guideline" arrow>
              <IconButton
                size="small"
                onClick={onImport}
                sx={{
                  color: "#1F5AF2",
                  "&:hover": { bgcolor: "#eef4f7" },
                }}
              >
                <UploadFileOutlined sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
        </Flx>
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search guidelines…"
          size="small"
          fullWidth
          variant="filled"
          suppressBorder
        />
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 1.25, py: 3 }}>
        {loading ? (
          <Flx center sx={{ py: 5 }}>
            <CircularProgress size={20} sx={{ color: "#1F5AF2" }} />
          </Flx>
        ) : groups.length === 0 ? (
          <Flx
            column
            center
            sx={{ py: 5, px: 2, textAlign: "center", color: "#94a3b8" }}
          >
            <MenuBookOutlined sx={{ fontSize: 30, mb: 1 }} />
            <Txt sx={{ fontSize: 12.5 }}>
              {search
                ? "No guidelines match your search."
                : "No guidelines published."}
            </Txt>
          </Flx>
        ) : (
          groups.map(([program, docs], i) => (
            <Box
              key={program}
              sx={{
                mt: i === 0 ? 0 : 1.5,
                pt: i === 0 ? 0 : 1.25,
                borderTop: i === 0 ? "none" : "1px solid #d8dee4",
              }}
            >
              <Flx ac g={0.75} sx={{ px: 0.5, mb: 0.75 }}>
                <Box
                  sx={{
                    width: 3,
                    height: 12,
                    borderRadius: 2,
                    bgcolor: "#1F5AF2",
                  }}
                />
                <Txt
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    color: "#475569",
                  }}
                >
                  {program}
                </Txt>
              </Flx>
              <Box sx={{ pl: 0.75 }}>
                {docs.map((doc) => (
                  <GuidelineRow
                    key={doc.slug}
                    doc={doc}
                    selected={doc.slug === selectedSlug}
                    onSelect={onSelect}
                  />
                ))}
              </Box>
            </Box>
          ))
        )}
      </Box>
    </Flx>
  );
}
